import { useState, useCallback } from 'react';
import { useApiService } from '../context/ApiServiceContext';
import { useSelector } from 'react-redux';
import { RootState } from '../utils/store';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Represents a parsed field value from the Excel import.
 */
export interface ExcelFieldValue {
    type: 'text' | 'asset';
    value?: string;
    assetId?: string;
    name?: string;
    thumbnail?: string;
    highRes?: string;
    width?: number;
    height?: number;
    assetType?: string;
    duration?: number;
}

/**
 * Response from the Excel import API.
 */
interface ExcelImportResponse {
    fields: Record<string, ExcelFieldValue>;
}

/**
 * Return type for the useExcelImport hook.
 */
interface UseExcelImportResult {
    /** Whether the current user's team has Excel import/export enabled */
    canUseExcel: boolean;
    /** Whether an Excel template is currently being downloaded */
    isDownloading: boolean;
    /** Whether an Excel file is currently being uploaded/parsed */
    isUploading: boolean;
    /** Downloads the Excel template for the given template ID */
    downloadTemplate: (templateId: string) => Promise<void>;
    /** Uploads and parses an Excel file, returning the parsed field values */
    uploadAndParse: (templateId: string, file: File) => Promise<Record<string, ExcelFieldValue> | null>;
}

/**
 * Hook for Excel template import/export functionality.
 *
 * Provides methods to download an Excel template for a given template ID,
 * and to upload a filled Excel file to parse and populate field values.
 *
 * @returns {UseExcelImportResult} Object containing permission status, loading states, and methods
 */
export const useExcelImport = (): UseExcelImportResult => {
    const apiService = useApiService();
    const user = useSelector((state: RootState) => state.user.user);
    const { t } = useTranslation();

    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Check if the user's team has Excel support enabled
    const canUseExcel = user?.team?.supportsExcel || false;

    /**
     * Downloads the Excel template for the given template ID.
     * The file will be downloaded as `template_{templateId}.xlsx`.
     */
    const downloadTemplate = useCallback(async (templateId: string): Promise<void> => {
        if (!canUseExcel) {
            toast.error(t('excel.error.noPermission'));
            return;
        }

        setIsDownloading(true);
        try {
            const response = await apiService.api.get(`/template/${templateId}/excel`, {
                responseType: 'blob'
            });

            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `template_${templateId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(t('excel.success.downloaded'));
        } catch (error: any) {
            console.error('Excel download failed:', error);
            const errorMessage = error?.response?.data?.message || t('excel.error.downloadFailed');
            toast.error(errorMessage);
        } finally {
            setIsDownloading(false);
        }
    }, [apiService, canUseExcel, t]);

    /**
     * Uploads and parses an Excel file for the given template ID.
     *
     * @param templateId - The ID of the template
     * @param file - The Excel file to upload
     * @returns The parsed field values, or null if the upload failed
     */
    const uploadAndParse = useCallback(async (
        templateId: string,
        file: File
    ): Promise<Record<string, ExcelFieldValue> | null> => {
        if (!canUseExcel) {
            toast.error(t('excel.error.noPermission'));
            return null;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiService.api.post<ExcelImportResponse>(
                `/template/${templateId}/excel/import`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            toast.success(t('excel.success.imported'));
            return response.data.fields;
        } catch (error: any) {
            console.error('Excel upload failed:', error);
            const errorMessage = error?.response?.data?.message || t('excel.error.uploadFailed');
            toast.error(errorMessage);
            return null;
        } finally {
            setIsUploading(false);
        }
    }, [apiService, canUseExcel, t]);

    return {
        canUseExcel,
        isDownloading,
        isUploading,
        downloadTemplate,
        uploadAndParse
    };
};

export default useExcelImport;
